import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RoomStatus, RoomStatusLabels } from 'src/app/common/enums/room-status';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { MaterialModule } from 'src/app/material.module';
import { BedService } from '../bed.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { LookupService } from 'src/app/services/lookup.service';
import { IBed } from '../ibed';
import { ToastrService } from 'ngx-toastr';
import { TablerIconsModule } from 'angular-tabler-icons';

@Component({
  selector: 'app-bed-detail',
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    TranslateModule,
    TablerIconsModule
  ],
  templateUrl: './bed-detail.component.html',
  styleUrl: './bed-detail.component.scss'
})
export class BedDetailComponent {
  bedForm: FormGroup;
  isEdit: boolean;

  roomNumberddl: CommonDropDown[] = [];
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  public roomStatusData = RoomStatusLabels;
  public roomstatusList = Object.values(RoomStatus).filter(value => typeof value === 'number');

  constructor(
    private _bedService: BedService,
    private _formbuilder: FormBuilder,
    private dialogRef: MatDialogRef<BedDetailComponent>,
    private toastr: ToastrService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils,
    private _lookupService: LookupService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) private _data: { bed: IBed, isEdit: boolean, roomId?: number },
  ) {
    this.isEdit = _data?.isEdit;
  }

  ngOnInit(): void {

    // Load room numbers directly to ensure fresh data
    this._lookupService.getRoomNumber()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.roomNumberddl = data || [];
      });

    const initialRoomId = this.isEdit ? this._data?.bed?.roomId : (this._data?.roomId || '');

    this.bedForm = this._formbuilder.group({
      roomId: [initialRoomId, [Validators.required]],
      bedNumber: [this._data?.bed?.bedNumber || '', [Validators.required, Validators.maxLength(5)]],
      status: [RoomStatus[this._data?.bed?.status as any as keyof typeof RoomStatus] ?? RoomStatus.Available],
      isActive: [this._data?.bed?.isActive ?? true]
    })

    if (initialRoomId) {
      this.bedForm.get('roomId')?.disable();
    } else {
      this.bedForm.get('roomId')?.enable();
    }
  }

  onAddBed() {
    const data = this.bedForm.getRawValue();
    this._bedService.createBed(data).subscribe((response) => {
      if (response.isSuccess) {
        this.dialogRef.close(response);
        this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.CREATE', this.translate.instant('ENTITY.BED')));
      }
      else {
        const allErrors = Object.values(response.errors).flat();
        this.toastr.error(allErrors.join("\n"));
      }
    });
  }

  onUpdateBed() {
    const data = {
      id: this._data?.bed?.id,
      ...this.bedForm.getRawValue()
    };

    this._bedService.updateBed(data).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.dialogRef.close(response);
          this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.UPDATE', this.translate.instant('ENTITY.BED')));
        }
      },
      error: (error) => {
        if (error?.error?.errors) {
          const allErrors = Object.values(error.error.errors).flat();
          this.toastr.error(allErrors.join("\n"));
        } else {
          this.toastr.error('Something went wrong');
        }
      }
    });
  }

  onCloseDialog(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
